<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import { SIDEBAR_ASSETS, accentColor } from './unifiedSidebar';

const props = defineProps({
  products: { type: Array, required: true },
  activeProductKey: { type: String, required: true },
  collapsed: { type: Boolean, default: false },
  menuHovered: { type: Boolean, default: false },
});

const emit = defineEmits([
  'select-product',
  'toggle-collapse',
  'open-key-shortcut-modal',
]);

const { t } = useI18n();
const router = useRouter();
const notificationMetadata = useMapGetter('notifications/getMeta');

const collapseLabel = computed(() =>
  props.collapsed ? t('SIDEBAR.SHOW_SIDEBAR') : t('SIDEBAR.HIDE_SIDEBAR')
);

const unreadCount = computed(() => {
  const count = notificationMetadata.value?.unreadCount;
  if (!count) return '';
  return count < 100 ? `${count}` : '99+';
});

const goToNotifications = () => {
  router.push({ name: 'notifications_index' });
};

const productButtonStyle = product => {
  const color = accentColor(product.accent);
  const active = product.key === props.activeProductKey;
  return {
    backgroundColor: active
      ? `color-mix(in srgb, ${color} 12%, transparent)`
      : 'transparent',
  };
};
</script>

<template>
  <div
    class="hidden md:flex flex-col items-center justify-between flex-shrink-0 w-[3.25rem] pt-[0.85rem] pb-3 ltr:border-r rtl:border-l border-n-weak bg-white dark:bg-n-solid-2"
  >
    <div class="flex flex-col items-center gap-4 w-full">
      <button
        type="button"
        class="relative flex items-center justify-center size-[2.35rem] p-0 border-0 rounded-lg bg-transparent cursor-pointer text-n-slate-12 hover:bg-n-brand/10 hover:text-n-brand"
        :title="menuHovered ? collapseLabel : 'Leadster'"
        :aria-label="menuHovered ? collapseLabel : 'Leadster'"
        :aria-expanded="!collapsed"
        @click="emit('toggle-collapse')"
      >
        <img
          :src="SIDEBAR_ASSETS.mark"
          width="28"
          height="24"
          alt="Leadster"
          class="object-contain max-h-6 transition-opacity duration-150 pointer-events-none"
          :class="menuHovered ? 'opacity-0' : 'opacity-100'"
        />
        <span
          class="absolute inset-0 flex items-center justify-center transition-opacity duration-150 pointer-events-none"
          :class="menuHovered ? 'opacity-100' : 'opacity-0'"
          aria-hidden="true"
        >
          <span
            class="size-[1.35rem]"
            :class="
              collapsed
                ? 'i-lucide-panel-left-open'
                : 'i-lucide-panel-left-close'
            "
          />
        </span>
      </button>

      <div class="flex flex-col items-center gap-[0.65rem] w-full">
        <button
          v-for="product in products"
          :key="product.key"
          type="button"
          class="inline-flex items-center justify-center size-[2.35rem] border-0 rounded-full p-0 cursor-pointer hover:opacity-90"
          :style="productButtonStyle(product)"
          :title="product.label"
          :aria-label="product.label"
          :aria-pressed="product.key === activeProductKey"
          @click="emit('select-product', product)"
        >
          <img
            :src="product.iconUrl"
            alt=""
            aria-hidden="true"
            class="size-[1.35rem] object-contain block"
          />
        </button>
      </div>
    </div>

    <div class="flex flex-col items-center gap-[0.35rem] w-full pt-2">
      <button
        type="button"
        class="relative flex items-center justify-center size-[2.35rem] rounded-full border-0 bg-transparent text-n-slate-12 hover:bg-[var(--sidebar-hover-bg)] cursor-pointer"
        :title="t('SIDEBAR.NOTIFICATIONS')"
        :aria-label="t('SIDEBAR.NOTIFICATIONS')"
        @click="goToNotifications"
      >
        <span class="i-lucide-bell size-5" />
        <span
          v-if="unreadCount"
          class="min-h-2 min-w-2 p-0.5 px-1 bg-n-ruby-9 rounded-md absolute -top-0.5 -right-0.5 grid place-items-center text-[9px] leading-none text-n-ruby-3"
        >
          {{ unreadCount }}
        </span>
      </button>

      <SidebarProfileMenu
        collapsed
        @open-key-shortcut-modal="emit('open-key-shortcut-modal')"
      />
    </div>
  </div>
</template>
